import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, MessageCircleHeart, ShieldCheck } from "lucide-react";
import ThemeToggle, { useTheme } from "./ThemeToggle";
import { useAuth } from "../context/AuthContext";
import { trackEvent } from "../lib/api";

const NAV = [
  { to: "/listings", label: "Listings" },
  { to: "/how-it-works", label: "How it Works" },
  { to: "/for-landlords", label: "For Landlords" },
  { to: "/faq", label: "FAQ" },
  { to: "/blog", label: "Blog" },
];

function Logo({ className = "h-9" }) {
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center" aria-label="Tendly home">
      <img src="/logo.png" alt="Tendly — Home search, handled" className={`${className} w-auto rounded-lg bg-white px-2 py-1`} />
    </Link>
  );
}

export default function Layout() {
  const [dark, setDark] = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [cookies, setCookies] = useState(() => localStorage.getItem("tendly-cookies"));
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const firstView = useRef(true);

  useEffect(() => {
    trackEvent("page_view", {
      path: location.pathname,
      ...(firstView.current && document.referrer ? { referrer: document.referrer } : {}),
    });
    firstView.current = false;
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => {
      const el = e.target.closest?.("[data-testid]");
      const tid = el?.getAttribute("data-testid") || "";
      if (/open-chat|cta|find-homes|submit-viewings|blog-card-/.test(tid)) {
        trackEvent("click", { path: window.location.pathname, target: tid });
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-3 z-50 px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto w-full">
        <nav className="flex items-center justify-between gap-4 rounded-2xl border border-border/60 bg-card/80 backdrop-blur-xl px-4 sm:px-6 py-3 shadow-sm" aria-label="Main navigation">
          <Logo />
          <div className="hidden lg:flex items-center gap-6">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `text-sm font-medium transition-colors hover:text-primary ${isActive ? "text-primary" : "text-muted-foreground"}`}>
                {n.label}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle dark={dark} onToggle={setDark} />
            {user && (user.role === "admin" || user.role === "agent") && (
              <button data-testid="nav-dashboard" onClick={() => navigate("/dashboard")}
                className="hidden sm:block text-sm font-medium text-muted-foreground hover:text-primary transition-colors">Dashboard</button>
            )}
            <Link to="/chat" data-testid="nav-open-chat"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-semibold hover:bg-coral-600 transition-colors">
              <MessageCircleHeart className="w-4 h-4" /> Open live chat
            </Link>
            <button data-testid="mobile-menu-toggle" className="lg:hidden p-2" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu" aria-expanded={menuOpen}>
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </nav>
        {menuOpen && (
          <div className="lg:hidden mt-2 rounded-2xl border border-border bg-card/95 backdrop-blur-xl p-4 flex flex-col gap-3 shadow-lg">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMenuOpen(false)} data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="flex items-center gap-3 px-4 py-3.5 min-h-[44px] text-base font-medium rounded-xl hover:bg-secondary active:bg-secondary/80 transition-colors">{n.label}</Link>
            ))}
            <Link to="/chat" onClick={() => setMenuOpen(false)} data-testid="mobile-open-chat"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-3 min-h-[44px] text-sm font-semibold">
              <MessageCircleHeart className="w-4 h-4" /> Open live chat
            </Link>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet context={{ dark }} />
      </main>

      <footer className="mt-24 border-t border-border bg-card">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2 md:col-span-1">
            <Logo className="h-10" />
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">Your AI rental concierge for Vancouver. Free for renters, always.</p>
            <p className="mt-4 text-xs text-muted-foreground leading-relaxed">
              Tendly<br />Kitsilano, BC, Canada<br />
              <a href="mailto:tendly.concierge@gmail.com" className="text-primary hover:underline">tendly.concierge@gmail.com</a><br />+1 (778) 956-6372
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-teal-50 dark:bg-teal-900/40 text-primary px-3 py-1 text-xs font-semibold">
              <ShieldCheck className="w-3.5 h-3.5" /> Free for renters, always
            </span>
          </div>
          <nav aria-label="Explore">
            <h4 className="text-xs uppercase tracking-wider font-semibold text-primary/80 mb-4">Explore</h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li><Link to="/listings" className="hover:text-primary">Browse listings</Link></li>
              <li><Link to="/rentals/kitsilano" className="hover:text-primary">Rentals in Kitsilano</Link></li>
              <li><Link to="/rentals/downtown" className="hover:text-primary">Rentals Downtown</Link></li>
              <li><Link to="/rentals/mount-pleasant" className="hover:text-primary">Rentals in Mount Pleasant</Link></li>
              <li><Link to="/chat" className="hover:text-primary">AI concierge chat</Link></li>
            </ul>
          </nav>
          <nav aria-label="Company">
            <h4 className="text-xs uppercase tracking-wider font-semibold text-primary/80 mb-4">Company</h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li><Link to="/about" className="hover:text-primary">About Tendly</Link></li>
              <li><Link to="/how-it-works" className="hover:text-primary">How it works</Link></li>
              <li><Link to="/for-landlords" className="hover:text-primary">For landlords</Link></li>
              <li><Link to="/blog" className="hover:text-primary">Blog</Link></li>
              <li><Link to="/contact" className="hover:text-primary">Contact</Link></li>
              <li><Link to="/account" className="hover:text-primary">Renter account</Link></li>
            </ul>
          </nav>
          <nav aria-label="Legal">
            <h4 className="text-xs uppercase tracking-wider font-semibold text-primary/80 mb-4">Legal</h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li><Link to="/privacy" className="hover:text-primary">Privacy Policy (PIPEDA)</Link></li>
              <li><Link to="/privacy/delete" className="hover:text-primary">Delete my data</Link></li>
              <li><Link to="/terms" className="hover:text-primary">Terms of Service</Link></li>
              <li><Link to="/faq" className="hover:text-primary">FAQ</Link></li>
            </ul>
          </nav>
        </div>
        <div className="border-t border-border">
          <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>© 2026 Tendly. All rights reserved.</span>
            <span>Tendly is a free rental concierge for renters.</span>
          </div>
        </div>
      </footer>

      {!cookies && (
        <div data-testid="cookie-banner" className="fixed bottom-20 left-4 right-4 sm:bottom-4 sm:left-auto sm:max-w-md z-50 rounded-2xl border border-border bg-card shadow-xl p-5">
          <p className="text-sm leading-relaxed">We use essential cookies and privacy-friendly analytics to improve Tendly. No advertising trackers.</p>
          <div className="mt-3 flex gap-2">
            <button data-testid="cookie-accept" onClick={() => { localStorage.setItem("tendly-cookies", "all"); setCookies("all"); }}
              className="rounded-full bg-primary text-primary-foreground px-4 py-1.5 text-sm font-semibold">Accept</button>
            <button data-testid="cookie-essential" onClick={() => { localStorage.setItem("tendly-cookies", "essential"); setCookies("essential"); }}
              className="rounded-full border border-border px-4 py-1.5 text-sm font-medium">Essential only</button>
          </div>
        </div>
      )}
    </div>
  );
}
